import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Check, CheckCheck } from "lucide-react";

const TIPO_ICON = {
  estado_cambio: "🔄",
  nuevo_diseno: "✏️",
  nuevo_produccion: "🏭",
  aaff_recibido: "✅",
  produccion_completada: "📦",
  recepcion_pendiente: "🚚",
  recepcion_completada: "📥",
  mencion: "💬",
};

export default function NotificationsBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [panelPos, setPanelPos] = useState({ top: 0, right: 0 });
  const ref = useRef(null);
  const btnRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notificaciones?limit=20");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // poll cada 30s
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      // permitir clicks dentro del panel (Portal está fuera del ref)
      const panel = document.getElementById("notifications-panel-portal");
      if (panel && panel.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Calcular posición del panel cuando se abre
  useEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setPanelPos({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
    // Reposicionar al hacer scroll/resize
    const reposition = () => {
      if (!btnRef.current) return;
      const r = btnRef.current.getBoundingClientRect();
      setPanelPos({ top: r.bottom + 8, right: window.innerWidth - r.right });
    };
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  const markAll = async () => {
    await api.post("/notificaciones/mark-read", { all: true });
    setUnread(0);
    setItems((items) => items.map((i) => ({ ...i, leida: true })));
  };

  const click = async (n) => {
    if (!n.leida) {
      await api.post("/notificaciones/mark-read", { ids: [n.id] });
      setUnread((u) => Math.max(0, u - 1));
    }
    setOpen(false);
    if (n.url) nav(n.url);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        ref={btnRef}
        data-testid="notifications-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-md text-zinc-400 hover:text-amber-300 hover:bg-zinc-900/70 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unread > 0 && (
          <span
            data-testid="notifications-badge"
            className="absolute -top-0.5 -right-0.5 bg-amber-500 text-zinc-900 text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          id="notifications-panel-portal"
          data-testid="notifications-panel"
          style={{ position: "fixed", top: panelPos.top, right: panelPos.right, zIndex: 9999 }}
          className="w-[380px] max-w-[calc(100vw-2rem)] max-h-[500px] bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
            <div className="text-sm font-semibold">
              Notificaciones {unread > 0 && <span className="text-amber-400">({unread})</span>}
            </div>
            {unread > 0 && (
              <button
                onClick={markAll}
                data-testid="notif-mark-all-read"
                className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1"
              >
                <CheckCheck className="w-3.5 h-3.5" /> Marcar leídas
              </button>
            )}
          </div>
          <div className="max-h-[440px] overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-8 text-center text-sm text-zinc-500">
                Sin notificaciones
              </div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => click(n)}
                data-testid={`notif-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-zinc-800/70 hover:bg-zinc-800/40 transition-colors ${
                  !n.leida ? "bg-amber-500/5" : ""
                }`}
              >
                <div className="flex gap-2.5">
                  <div className="text-base leading-none mt-0.5">
                    {TIPO_ICON[n.tipo] || "•"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-zinc-200 flex items-center gap-1.5">
                      {n.titulo}
                      {!n.leida && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
                    </div>
                    <div className="text-xs text-zinc-400 mt-0.5 truncate">{n.mensaje}</div>
                    <div className="text-[10px] text-zinc-600 mt-1">
                      {new Date(n.created_at).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
